import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import * as React from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import {
  MapDrawIDEnum,
  selectCurrMapDrawFeatures,
  setCurrMapDrawID,
} from '../../onboardingSlice';
import { isValidGeoFeature } from '../../onboardingUtils';
// Create an array of the enum values in the same order as the steps array.  Filter out the blockBoundary enum value since that is handled in a separate tab now instead of a step in the stepper.
const mapIDEnums = Object.values(MapDrawIDEnum).filter(
  (val) => val !== MapDrawIDEnum.blockBoundary
);

const steps = ['Draw X Number Orientation', 'Draw Y Row Orientation'];

enum StepEnum {
  xNumberOrientation = 0,
  yRowOrientation = 1,
  done = 2,
}

export default function BlockDrawStepper() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currMapDrawFeatures = useAppSelector(selectCurrMapDrawFeatures);
  const [activeStep, setActiveStep] = React.useState(
    StepEnum.xNumberOrientation
  );
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});

  // Monitor the current map draw features and update the completed steps if the step has valid data
  React.useEffect(() => {
    const newCompleted = {
      [StepEnum.xNumberOrientation]: isValidGeoFeature(
        currMapDrawFeatures,
        'LineString',
        MapDrawIDEnum.xNumberOrientation
      ),
      [StepEnum.yRowOrientation]: isValidGeoFeature(
        currMapDrawFeatures,
        'LineString',
        MapDrawIDEnum.yRowOrientation
      ),
    };
    setCompleted(newCompleted);

    if (newCompleted[StepEnum.xNumberOrientation]) {
      setStepAndCurrMapDrawID(StepEnum.yRowOrientation);
    }

    // If x and y are completed, set the active step to done
    if (
      newCompleted[StepEnum.xNumberOrientation] &&
      newCompleted[StepEnum.yRowOrientation]
    ) {
      setStepAndCurrMapDrawID(StepEnum.done);
    }
  }, [currMapDrawFeatures]);

  // Set the current step and the current map draw ID in redux for tracking the feature ids on the map
  const setStepAndCurrMapDrawID = (step: number) => {
    setActiveStep(step);
    dispatch(setCurrMapDrawID(mapIDEnums[step]));
  };

  const handleStep = (step: number) => () => {
    setStepAndCurrMapDrawID(step);
  };

  return (
    <Box sx={{ width: '100%', minWidth: '15rem' }}>
      <Stepper nonLinear activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => (
          <Step
            key={label}
            completed={completed[index]}
            sx={{
              '& .MuiStepLabel-root .Mui-completed': {
                color: (theme) => theme.colors.status.green.main, // circle color (COMPLETED)
              },
              '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel':
                {
                  color: 'grey.500', // Just text label (COMPLETED)
                },
              '& .MuiStepLabel-root .Mui-active': {
                color: (theme) => theme.colors.secondary, // circle color (ACTIVE)
              },
              '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel':
                {
                  color: (theme) => theme.colors.secondary, // Just text label (ACTIVE)
                },
              '& .MuiStepLabel-root .Mui-active .MuiStepIcon-text': {
                fill: (theme) => theme.colors.common.white, // circle's number (ACTIVE)
              },
            }}
          >
            <StepButton
              color='inherit'
              onClick={handleStep(index)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t(label)}
            </StepButton>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
